import "./index.css";
import { Spinner } from "@shopify/polaris";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { loadUser } from "@vendor-app/app/_state/actionCreators/initialize";
import { useActiveOrganizationSelector } from "@vendor-app/app/_state/reducers/organizations";
import { useSmartrrVendorDispatch, useSmartrrVendorSelector } from "@vendor-app/app/_state/typedVendorReduxHooks";
import { initPendo } from "@vendor-app/external/pendo";
import { initChameleon } from "@vendor-app/external/chameleon";
import { InitIntercom } from "@vendor-app/external/intercom";
import { useRequiredAuthRedirects } from "@vendor-app/utils/shopify/handleRequiredAuthRedirects";
import { useLDIdentityVendor } from "@vendor-app/launchdarkly";
import { LDContext, withLDProvider } from "launchdarkly-react-client-sdk";
import config from "@vendor-app/config";
import { LaunchDarklyOptions } from "@smartrr/shared/LaunchDarkly";
import { isEqual } from "lodash";

interface Props {
  children: React.JSX.Element;
}

export function AdminAuthContainer({ children: Component }: Props): JSX.Element {
  const user = useSmartrrVendorSelector(state => state.auth.user);
  const userIsLoading = useSmartrrVendorSelector(state => state.auth.isLoading);
  const { activePlan, isLoadingActivePlan } = useSmartrrVendorSelector(state => state.accountPlans);
  const activeOrg = useActiveOrganizationSelector();
  const dispatch = useSmartrrVendorDispatch();
  const areProductsLoading = useSmartrrVendorSelector(state => state.purchasables).isLoading;
  const isRedirecting = useRequiredAuthRedirects(userIsLoading, user);
  const [LDProviderComponent, setLDProviderComponent] = useState<React.ComponentType<{}> | null>(null);
  const lastBuiltContextRef = useRef<LDContext | null>(null);

  const context = useLDIdentityVendor();

  useEffect(() => {
    if (!user && userIsLoading) {
      dispatch(loadUser());
    }
  }, []);

  useEffect(() => {
    if (!context || isEqual(context, lastBuiltContextRef.current)) {
      return;
    }

    lastBuiltContextRef.current = context;
    setLDProviderComponent(() =>
      withLDProvider({
        context,
        clientSideID: config.launchDarkly.clientSideId,
        ...LaunchDarklyOptions,
      })(() => {
        InitIntercom(user);

        return Component;
      })
    );
  }, [context]);

  useEffect(() => {
    if (!userIsLoading && !isRedirecting && activeOrg && user) {
      // pendo loaded into browser occurs in frontend/src/main
      initPendo({ authUser: user, activePlan, setup: activeOrg.setup, shopifyData: activeOrg.shopifyStoreData });
      initChameleon(user);
    }
  }, [userIsLoading, isRedirecting, activeOrg, user]);

  const content = useMemo(() => {
    if (!LDProviderComponent) {
      return <React.Fragment>{Component}</React.Fragment>;
    }
    return <LDProviderComponent />;
  }, [LDProviderComponent]);

  if (isRedirecting || userIsLoading || isLoadingActivePlan || areProductsLoading) {
    return (
      <div className="loader-container">
        <Spinner />
      </div>
    );
  }

  return content;
}
